import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, appId, functions } from '../firebase/firebaseConfig.jsx';
import { useUser } from '../contexts/UserContext';
import {
  ACCOUNTING_MENU_PATHS,
  getDefaultMenuPermissionsForRole,
  isSuperAdmin,
  validateAdminPassword,
} from '../utils/adminPermissions';
import { Shield, Users, Plus, Trash2, Edit, Save, X, RefreshCw, Mail, Lock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'admin', label: '관리자' },
  { value: 'accountant', label: '경리' },
  { value: 'staff', label: '스태프' },
  { value: 'ceo', label: 'CEO' },
  { value: 'cfo', label: 'CFO' },
];

const AdminUserManagementPage = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    displayName: '',
    role: 'admin',
    menuPermissions: [],
  });

  const allMenuPaths = useMemo(() => [
    { path: '/', name: 'Dashboard' },
    { path: '/kyc', name: 'KYC Management' },
    { path: '/rcc-issuance', name: t('sidebar.rccIssuance') },
    { path: '/policy-compliance', name: 'Policy Compliance' },
    { path: '/notifications', name: 'Push Notifications' },
    { path: '/notices', name: 'Notice Management' },
    { path: '/in-app-messaging', name: 'In-App Messaging' },
    { path: '/message-management', name: 'Message Management' },
    { path: '/users', name: 'User Management' },
    { path: '/document-expiry-management', name: 'Document Expiry Management' },
    { path: '/finance-settings', name: 'Finance Settings' },
    { path: '/community-management', name: 'Community Management' },
    { path: '/marketplace-management', name: '중고장터 관리' },
    { path: '/consultation-faq-management', name: 'Consultation FAQ Management' },
    { path: '/regional-poi-monitor', name: 'Regional POI Monitor' },
    { path: '/chat-cleanup', name: 'Chat Cleanup' },
    { path: '/api-integration-monitoring', name: 'API Integration' },
    { path: '/settlement-management', name: 'Settlement Management' },
    { path: '/remittance-accounts', name: 'Remittance Accounts' },
    { path: '/homepage-management', name: 'HomePage Management' },
    { path: '/help', name: 'Help' },
  ], [t]);

  const superAdmin = isSuperAdmin(user);

  useEffect(() => {
    if (!superAdmin) return;
    fetchAdminUsers();
  }, [superAdmin]);

  const fetchAdminUsers = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, `artifacts/${appId}/adminUsers`),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const usersData = [];

      querySnapshot.forEach((docSnap) => {
        usersData.push({
          id: docSnap.id,
          ...docSnap.data(),
        });
      });

      setAdminUsers(usersData);
    } catch (error) {
      console.error('Error fetching admin users:', error);
      alert('어드민 사용자 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const applyRoleDefaultPermissions = (role, currentPermissions = []) => {
    if (role === 'accountant') {
      return [...ACCOUNTING_MENU_PATHS];
    }
    return currentPermissions;
  };

  const handleRoleChange = (role, target = 'new') => {
    const defaultPermissions = applyRoleDefaultPermissions(role, []);
    if (target === 'new') {
      setNewUser((prev) => ({
        ...prev,
        role,
        menuPermissions: defaultPermissions,
      }));
      return;
    }

    setEditingUser((prev) => ({
      ...prev,
      role,
      menuPermissions: applyRoleDefaultPermissions(role, prev?.menuPermissions || []),
    }));
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.displayName) {
      alert('모든 필드를 입력해주세요.');
      return;
    }

    const passwordError = validateAdminPassword(newUser.password);
    if (passwordError) {
      alert(passwordError);
      return;
    }

    const emailNorm = String(newUser.email).trim().toLowerCase();
    const displayNameNorm = String(newUser.displayName).trim();
    const menuPermissions = newUser.menuPermissions.length > 0
      ? newUser.menuPermissions
      : getDefaultMenuPermissionsForRole(newUser.role);

    try {
      setSaving(true);
      const createUser = httpsCallable(functions, 'adminCreateUser');
      const result = await createUser({
        email: emailNorm,
        password: newUser.password,
        displayName: displayNameNorm,
        role: newUser.role,
        menuPermissions,
        adminPortal: true,
      });

      if (!result?.data?.uid) {
        throw new Error('Cloud Function이 uid를 반환하지 않았습니다.');
      }

      alert('어드민 사용자가 성공적으로 등록되었습니다.');
      setShowAddModal(false);
      setNewUser({ email: '', password: '', displayName: '', role: 'admin', menuPermissions: [] });
      fetchAdminUsers();
    } catch (error) {
      console.error('Error adding admin user:', error);
      const msg = error?.message || String(error);
      const code = error?.code;
      if (
        code === 'auth/email-already-in-use' ||
        /already-exists|email-already/i.test(msg)
      ) {
        alert('이미 등록된 이메일입니다.');
      } else {
        alert('어드민 사용자 등록 중 오류가 발생했습니다: ' + msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePermissions = async (userId, menuPermissions, role) => {
    try {
      setSaving(true);
      const updatePermissions = httpsCallable(functions, 'adminUpdatePortalPermissions');
      await updatePermissions({
        uid: userId,
        menuPermissions,
        role,
      });

      alert('권한이 성공적으로 업데이트되었습니다.');
      setEditingUser(null);
      fetchAdminUsers();
    } catch (error) {
      console.error('Error updating permissions:', error);
      alert('권한 업데이트 중 오류가 발생했습니다: ' + (error?.message || String(error)));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('정말 이 어드민 사용자를 삭제하시겠습니까? Auth 계정도 함께 삭제됩니다.')) return;

    try {
      setSaving(true);
      const deleteUser = httpsCallable(functions, 'adminDeleteUser');
      await deleteUser({ uid: userId, adminPortal: true });
      alert('어드민 사용자가 삭제되었습니다.');
      fetchAdminUsers();
    } catch (error) {
      console.error('Error deleting admin user:', error);
      alert('어드민 사용자 삭제 중 오류가 발생했습니다: ' + (error?.message || String(error)));
    } finally {
      setSaving(false);
    }
  };

  const toggleMenuPermission = (userPermissions, menuPath) => {
    if (userPermissions.includes(menuPath)) {
      return userPermissions.filter((p) => p !== menuPath);
    }
    return [...userPermissions, menuPath];
  };

  const renderPermissionSummary = (adminUser) => {
    const permissions = adminUser.menuPermissions?.length
      ? adminUser.menuPermissions
      : getDefaultMenuPermissionsForRole(adminUser.role);

    if (!permissions.length) {
      return <span className="text-gray-400 text-xs">권한 없음</span>;
    }

    return (
      <>
        {permissions.slice(0, 3).map((path) => {
          const menu = allMenuPaths.find((m) => m.path === path);
          return menu ? (
            <span key={path} className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded">
              {menu.name}
            </span>
          ) : null;
        })}
        {permissions.length > 3 && (
          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
            +{permissions.length - 3}
          </span>
        )}
      </>
    );
  };

  if (!superAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">접근 권한 없음</h2>
        <p className="text-gray-600">이 페이지는 Super Admin만 접근할 수 있습니다.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <Shield className="w-8 h-8 text-indigo-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">어드민 사용자 관리</h1>
            <p className="text-gray-600 mt-1">어드민 사용자를 등록하고 메뉴 권한을 관리합니다</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchAdminUsers}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            새로고침
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            어드민 사용자 추가
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">사용자 / 이메일</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">역할</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">메뉴 권한</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">작업</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {adminUsers.map((adminUser) => (
                <tr key={adminUser.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {adminUser.displayName || '이름 없음'}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">{adminUser.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {ROLE_OPTIONS.find((r) => r.value === adminUser.role)?.label || adminUser.role || '-'}
                  </td>
                  <td className="px-6 py-4">
                    {editingUser?.id === adminUser.id ? (
                      <div className="space-y-2">
                        <select
                          value={editingUser.role || 'admin'}
                          onChange={(e) => handleRoleChange(e.target.value, 'edit')}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        >
                          {ROLE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <div className="max-h-40 overflow-y-auto border border-gray-200 rounded p-2">
                          {allMenuPaths.map((menu) => {
                            const hasPermission = editingUser.menuPermissions?.includes(menu.path) || false;
                            return (
                              <label key={menu.path} className="flex items-center space-x-2 py-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={hasPermission}
                                  onChange={() => {
                                    setEditingUser({
                                      ...editingUser,
                                      menuPermissions: toggleMenuPermission(editingUser.menuPermissions || [], menu.path),
                                    });
                                  }}
                                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <span className="text-sm text-gray-700">{menu.name}</span>
                              </label>
                            );
                          })}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleUpdatePermissions(
                              adminUser.id,
                              editingUser.menuPermissions || [],
                              editingUser.role
                            )}
                            disabled={saving}
                            className="flex items-center gap-1 px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-50"
                          >
                            <Save className="w-3 h-3" />
                            저장
                          </button>
                          <button
                            onClick={() => setEditingUser(null)}
                            className="flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                          >
                            <X className="w-3 h-3" />
                            취소
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1">{renderPermissionSummary(adminUser)}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {adminUser.active ? (
                      <div className="flex items-center space-x-2 text-green-600 text-sm">
                        <CheckCircle className="w-4 h-4" />
                        <span>활성</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 text-red-600 text-sm">
                        <XCircle className="w-4 h-4" />
                        <span>비활성</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingUser({
                          ...adminUser,
                          menuPermissions: adminUser.menuPermissions?.length
                            ? adminUser.menuPermissions
                            : getDefaultMenuPermissionsForRole(adminUser.role),
                        })}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="권한 수정"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {adminUser.email !== 'kairos@kairos.com' && (
                        <button
                          onClick={() => handleDeleteUser(adminUser.id)}
                          disabled={saving}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          title="삭제"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">어드민 사용자 추가</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewUser({ email: '', password: '', displayName: '', role: 'admin', menuPermissions: [] });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">사용자 이름 *</label>
                <input
                  type="text"
                  value={newUser.displayName}
                  onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="홍길동"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">이메일 *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="hong@example.com"
                  />
                </div>
                <p className="mt-2 text-sm text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
                  {newUser.displayName.trim()
                    ? `등록 사용자: ${newUser.displayName.trim()}`
                    : '사용자 이름을 먼저 입력하세요.'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">비밀번호 *</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="8자 이상, 영문+숫자 포함"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">역할 *</label>
                <select
                  value={newUser.role}
                  onChange={(e) => handleRoleChange(e.target.value, 'new')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {ROLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {newUser.role === 'accountant' && (
                  <p className="text-xs text-indigo-600 mt-1">
                    경리 역할은 회계 관리 메뉴 권한이 자동으로 부여됩니다.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">메뉴 권한</label>
                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded p-3">
                  {allMenuPaths.map((menu) => {
                    const permissions = newUser.menuPermissions.length
                      ? newUser.menuPermissions
                      : getDefaultMenuPermissionsForRole(newUser.role);
                    const hasPermission = permissions.includes(menu.path);
                    return (
                      <label key={menu.path} className="flex items-center space-x-2 py-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={hasPermission}
                          onChange={() => {
                            setNewUser({
                              ...newUser,
                              menuPermissions: toggleMenuPermission(
                                newUser.menuPermissions.length
                                  ? newUser.menuPermissions
                                  : getDefaultMenuPermissionsForRole(newUser.role),
                                menu.path
                              ),
                            });
                          }}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">{menu.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewUser({ email: '', password: '', displayName: '', role: 'admin', menuPermissions: [] });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                취소
              </button>
              <button
                onClick={handleAddUser}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? '등록 중...' : '등록'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserManagementPage;
